import { deriveInterclubJobState } from './interclubSyncStatus';

export const ETATS_PREPARATION_INTERCLUB = Object.freeze({
  AGENT_PRET: 'AGENT_PRET',
  PREPARATION_EN_COURS: 'PREPARATION_EN_COURS',
  CONNEXION_REQUISE: 'CONNEXION_REQUISE',
  JOB_EN_COURS: 'JOB_EN_COURS',
  JOB_TERMINE: 'JOB_TERMINE',
  ERREUR_PREPARATION: 'ERREUR_PREPARATION',
});

export const TIMEOUT_PREPARATION_INTERCLUB_MS = 15000;

export class ErreurPreparationInterclub extends Error {
  constructor(code, message, details = {}) {
    super(message);
    this.name = 'ErreurPreparationInterclub';
    this.code = code;
    Object.assign(this, details);
  }
}

async function fetchAvecTimeout(fetchImpl, url, options, timeoutMs) {
  const controleur = typeof AbortController === 'function' ? new AbortController() : null;
  let expiration;
  const timeout = new Promise((resolve, reject) => {
    expiration = setTimeout(() => {
      controleur?.abort();
      reject(new ErreurPreparationInterclub(
        'PREPARATION_TIMEOUT',
        'PRÉPARATION INTERCLUB IMPOSSIBLE — délai dépassé.'
      ));
    }, timeoutMs);
  });
  try {
    return await Promise.race([
      fetchImpl(url, { ...options, ...(controleur ? { signal: controleur.signal } : {}) }),
      timeout,
    ]);
  } finally {
    clearTimeout(expiration);
  }
}

async function lireJsonLocal(fetchImpl, url, options, timeoutMs) {
  let response;
  try {
    response = await fetchAvecTimeout(fetchImpl, url, options, timeoutMs);
  } catch (error) {
    if (error?.code === 'PREPARATION_TIMEOUT') throw error;
    throw new ErreurPreparationInterclub('AGENT_INJOIGNABLE', 'AGENT LOCAL NON DÉMARRÉ', { cause: error });
  }
  const payload = await response.json().catch(() => ({}));
  return { response, payload };
}

export async function preparerEtCreerJobInterclub({
  fetchImpl = fetch,
  clientSupabase,
  groupe,
  chargerEtat,
  executionInitiale = null,
  timeoutMs = TIMEOUT_PREPARATION_INTERCLUB_MS,
}) {
  if (deriveInterclubJobState(executionInitiale) === 'RUNNING') {
    return { etat: ETATS_PREPARATION_INTERCLUB.JOB_EN_COURS, jobCree: false, execution: executionInitiale };
  }

  const { response: reponseSante, payload: sante } = await lireJsonLocal(
    fetchImpl,
    'http://127.0.0.1:43119/health',
    { cache: 'no-store' },
    timeoutMs
  );
  if (!reponseSante.ok || !sante.ok || !sante.version) {
    throw new ErreurPreparationInterclub('AGENT_INCOMPATIBLE', sante.error || 'agent local incompatible');
  }
  if (sante.state === 'ERREUR_RECUPERABLE') {
    throw new ErreurPreparationInterclub('ERREUR_RECUPERABLE', 'La fenêtre Interclub a été fermée.');
  }

  const { response: reponseLocale, payload: resultatLocal } = await lireJsonLocal(
    fetchImpl,
    'http://127.0.0.1:43119/prepare-direct',
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
    timeoutMs
  );
  if (!reponseLocale.ok || !resultatLocal.authenticated || resultatLocal.version !== sante.version) {
    const recuperable = resultatLocal.code === 'ERREUR_RECUPERABLE' || resultatLocal.state === 'ERREUR_RECUPERABLE';
    if (recuperable) throw new ErreurPreparationInterclub('ERREUR_RECUPERABLE', 'La fenêtre Interclub a été fermée.');
    if (!resultatLocal.authenticated) {
      throw new ErreurPreparationInterclub('CONNEXION_REQUISE', resultatLocal.error || 'CONNEXION INTERCLUB REQUISE');
    }
    throw new ErreurPreparationInterclub('PROTOCOLE_INCOHERENT', resultatLocal.error || 'versions de protocole incohérentes');
  }

  const etatServeur = await chargerEtat();
  const derniereActivite = etatServeur?.agent?.derniere_activite ? new Date(etatServeur.agent.derniere_activite).valueOf() : 0;
  const agentRecent = derniereActivite && Date.now() - derniereActivite < 5 * 60 * 1000;
  if (!agentRecent || etatServeur?.agent?.statut !== 'EN_LIGNE' || etatServeur?.agent?.version !== sante.version) {
    throw new ErreurPreparationInterclub('AGENT_NON_CONFIRME', 'agent Interclub non confirmé par le serveur');
  }
  if (deriveInterclubJobState(etatServeur?.execution) === 'RUNNING') {
    return { etat: ETATS_PREPARATION_INTERCLUB.JOB_EN_COURS, jobCree: false, execution: etatServeur.execution, sante };
  }

  const { data, error } = await clientSupabase.rpc('interclub_sync_directe_demander', { p_groupe: groupe });
  if (error) throw new ErreurPreparationInterclub('CREATION_JOB_REFUSEE', error.message);
  const etatFinal = await chargerEtat();
  return {
    etat: ETATS_PREPARATION_INTERCLUB.JOB_EN_COURS,
    jobCree: true,
    jobId: etatFinal?.execution?.id || data?.id || data || null,
    execution: etatFinal?.execution || null,
    sante,
  };
}
